/**
 * Generated by orval v8.22.0 🍺
 * Do not edit manually.
 * PepsiCo Geospatial UAV API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AnalyzeSnipsRequest,
  GetAllTasksParams,
  GetTasksForCurrentUserParams,
  HTTPValidationError,
  MarkTaskAsFailedParams,
  TaskBulkMutationResponse,
  TaskCompleteBulkRequest,
  TaskCreate,
  TaskFailBulkRequest,
  TaskResponse,
  TaskResultsInput,
  TaskUpdate,
  TaskUpdateBulkRequest,
} from "../model";

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type createTaskResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type createTaskResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type createTaskResponseSuccess = createTaskResponse200 & {
  headers: Headers;
};
export type createTaskResponseError = createTaskResponse422 & {
  headers: Headers;
};

export type createTaskResponse =
  createTaskResponseSuccess | createTaskResponseError;

export const getCreateTaskUrl = () => {
  return `/api/tasks/`;
};

/**
 * @summary Create Task Route
 */
export const createTask = async (
  taskCreate: TaskCreate,
  options?: RequestInit,
): Promise<createTaskResponse> => {
  const res = await fetch(getCreateTaskUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskCreate),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createTaskResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createTaskResponse;
};

export const getCreateTaskMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTask>>,
    TError,
    { data: TaskCreate },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTask>>,
  TError,
  { data: TaskCreate },
  TContext
> => {
  const mutationKey = ["createTask"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTask>>,
    { data: TaskCreate }
  > = (props) => {
    const { data } = props ?? {};

    return createTask(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTaskMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTask>>
>;
export type CreateTaskMutationBody = TaskCreate;
export type CreateTaskMutationError = HTTPValidationError;

/**
 * @summary Create Task Route
 */
export const useCreateTask = <TError = HTTPValidationError, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createTask>>,
      TError,
      { data: TaskCreate },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createTask>>,
  TError,
  { data: TaskCreate },
  TContext
> => {
  return useMutation(getCreateTaskMutationOptions(options), queryClient);
};
export type getAllTasksResponse200 = {
  data: TaskResponse[];
  status: 200;
};

export type getAllTasksResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getAllTasksResponseSuccess = getAllTasksResponse200 & {
  headers: Headers;
};
export type getAllTasksResponseError = getAllTasksResponse422 & {
  headers: Headers;
};

export type getAllTasksResponse =
  getAllTasksResponseSuccess | getAllTasksResponseError;

export const getGetAllTasksUrl = (params?: GetAllTasksParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const arrayFormatParameters = ["task_type"];

    if (Array.isArray(value) && arrayFormatParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? "null" : String(v));
      });
      return;
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/tasks/?${stringifiedParams}`
    : `/api/tasks/`;
};

/**
 * @summary Get All Tasks
 */
export const getAllTasks = async (
  params?: GetAllTasksParams,
  options?: RequestInit,
): Promise<getAllTasksResponse> => {
  const res = await fetch(getGetAllTasksUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getAllTasksResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getAllTasksResponse;
};

export const getGetAllTasksQueryKey = (params?: GetAllTasksParams) => {
  return [`/api/tasks/`, ...(params ? [params] : [])] as const;
};

export const getGetAllTasksQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllTasks>>,
  TError = HTTPValidationError,
>(
  params?: GetAllTasksParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllTasks>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllTasksQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllTasks>>> = ({
    signal,
  }) => getAllTasks(params, { signal, ...fetchOptions });

  return {
    queryKey,
    queryFn,
    staleTime: 10000,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllTasks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllTasksQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllTasks>>
>;
export type GetAllTasksQueryError = HTTPValidationError;

export function useGetAllTasks<
  TData = Awaited<ReturnType<typeof getAllTasks>>,
  TError = HTTPValidationError,
>(
  params: undefined | GetAllTasksParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllTasks>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllTasks>>,
          TError,
          Awaited<ReturnType<typeof getAllTasks>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAllTasks<
  TData = Awaited<ReturnType<typeof getAllTasks>>,
  TError = HTTPValidationError,
>(
  params?: GetAllTasksParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllTasks>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllTasks>>,
          TError,
          Awaited<ReturnType<typeof getAllTasks>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAllTasks<
  TData = Awaited<ReturnType<typeof getAllTasks>>,
  TError = HTTPValidationError,
>(
  params?: GetAllTasksParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllTasks>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get All Tasks
 */

export function useGetAllTasks<
  TData = Awaited<ReturnType<typeof getAllTasks>>,
  TError = HTTPValidationError,
>(
  params?: GetAllTasksParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllTasks>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetAllTasksQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getTasksForCurrentUserResponse200 = {
  data: TaskResponse[];
  status: 200;
};

export type getTasksForCurrentUserResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getTasksForCurrentUserResponseSuccess =
  getTasksForCurrentUserResponse200 & {
    headers: Headers;
  };
export type getTasksForCurrentUserResponseError =
  getTasksForCurrentUserResponse422 & {
    headers: Headers;
  };

export type getTasksForCurrentUserResponse =
  getTasksForCurrentUserResponseSuccess | getTasksForCurrentUserResponseError;

export const getGetTasksForCurrentUserUrl = (
  params?: GetTasksForCurrentUserParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const arrayFormatParameters = ["task_type"];

    if (Array.isArray(value) && arrayFormatParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? "null" : String(v));
      });
      return;
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/tasks/me?${stringifiedParams}`
    : `/api/tasks/me`;
};

/**
 * @summary Get Tasks For Current User
 */
export const getTasksForCurrentUser = async (
  params?: GetTasksForCurrentUserParams,
  options?: RequestInit,
): Promise<getTasksForCurrentUserResponse> => {
  const res = await fetch(getGetTasksForCurrentUserUrl(params), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getTasksForCurrentUserResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getTasksForCurrentUserResponse;
};

export const getGetTasksForCurrentUserQueryKey = (
  params?: GetTasksForCurrentUserParams,
) => {
  return [`/api/tasks/me`, ...(params ? [params] : [])] as const;
};

export const getGetTasksForCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getTasksForCurrentUser>>,
  TError = HTTPValidationError,
>(
  params?: GetTasksForCurrentUserParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTasksForCurrentUser>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTasksForCurrentUserQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTasksForCurrentUser>>
  > = ({ signal }) =>
    getTasksForCurrentUser(params, { signal, ...fetchOptions });

  return {
    queryKey,
    queryFn,
    staleTime: 10000,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTasksForCurrentUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTasksForCurrentUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTasksForCurrentUser>>
>;
export type GetTasksForCurrentUserQueryError = HTTPValidationError;

export function useGetTasksForCurrentUser<
  TData = Awaited<ReturnType<typeof getTasksForCurrentUser>>,
  TError = HTTPValidationError,
>(
  params: undefined | GetTasksForCurrentUserParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTasksForCurrentUser>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTasksForCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getTasksForCurrentUser>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTasksForCurrentUser<
  TData = Awaited<ReturnType<typeof getTasksForCurrentUser>>,
  TError = HTTPValidationError,
>(
  params?: GetTasksForCurrentUserParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTasksForCurrentUser>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTasksForCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getTasksForCurrentUser>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTasksForCurrentUser<
  TData = Awaited<ReturnType<typeof getTasksForCurrentUser>>,
  TError = HTTPValidationError,
>(
  params?: GetTasksForCurrentUserParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTasksForCurrentUser>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Tasks For Current User
 */

export function useGetTasksForCurrentUser<
  TData = Awaited<ReturnType<typeof getTasksForCurrentUser>>,
  TError = HTTPValidationError,
>(
  params?: GetTasksForCurrentUserParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTasksForCurrentUser>>,
        TError,
        TData
      >
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetTasksForCurrentUserQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type markTasksAsCompletedResponse200 = {
  data: TaskBulkMutationResponse;
  status: 200;
};

export type markTasksAsCompletedResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type markTasksAsCompletedResponseSuccess =
  markTasksAsCompletedResponse200 & {
    headers: Headers;
  };
export type markTasksAsCompletedResponseError =
  markTasksAsCompletedResponse422 & {
    headers: Headers;
  };

export type markTasksAsCompletedResponse =
  markTasksAsCompletedResponseSuccess | markTasksAsCompletedResponseError;

export const getMarkTasksAsCompletedUrl = () => {
  return `/api/tasks/complete`;
};

/**
 * Superseded tasks are left unchanged and returned in ``superseded``. Other tasks are completed. Missing ids are returned in ``not_found``.
 * @summary Mark many tasks as completed
 */
export const markTasksAsCompleted = async (
  taskCompleteBulkRequest: TaskCompleteBulkRequest,
  options?: RequestInit,
): Promise<markTasksAsCompletedResponse> => {
  const res = await fetch(getMarkTasksAsCompletedUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskCompleteBulkRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: markTasksAsCompletedResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as markTasksAsCompletedResponse;
};

export const getMarkTasksAsCompletedMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markTasksAsCompleted>>,
    TError,
    { data: TaskCompleteBulkRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markTasksAsCompleted>>,
  TError,
  { data: TaskCompleteBulkRequest },
  TContext
> => {
  const mutationKey = ["markTasksAsCompleted"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markTasksAsCompleted>>,
    { data: TaskCompleteBulkRequest }
  > = (props) => {
    const { data } = props ?? {};

    return markTasksAsCompleted(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkTasksAsCompletedMutationResult = NonNullable<
  Awaited<ReturnType<typeof markTasksAsCompleted>>
>;
export type MarkTasksAsCompletedMutationBody = TaskCompleteBulkRequest;
export type MarkTasksAsCompletedMutationError = HTTPValidationError;

/**
 * @summary Mark many tasks as completed
 */
export const useMarkTasksAsCompleted = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markTasksAsCompleted>>,
      TError,
      { data: TaskCompleteBulkRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markTasksAsCompleted>>,
  TError,
  { data: TaskCompleteBulkRequest },
  TContext
> => {
  return useMutation(
    getMarkTasksAsCompletedMutationOptions(options),
    queryClient,
  );
};
export type markTasksAsFailedResponse200 = {
  data: TaskBulkMutationResponse;
  status: 200;
};

export type markTasksAsFailedResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type markTasksAsFailedResponseSuccess = markTasksAsFailedResponse200 & {
  headers: Headers;
};
export type markTasksAsFailedResponseError = markTasksAsFailedResponse422 & {
  headers: Headers;
};

export type markTasksAsFailedResponse =
  markTasksAsFailedResponseSuccess | markTasksAsFailedResponseError;

export const getMarkTasksAsFailedUrl = () => {
  return `/api/tasks/fail`;
};

/**
 * Superseded tasks are left unchanged and returned in ``superseded``. Other tasks are failed. Missing ids are returned in ``not_found``.
 * @summary Mark many tasks as failed
 */
export const markTasksAsFailed = async (
  taskFailBulkRequest: TaskFailBulkRequest,
  options?: RequestInit,
): Promise<markTasksAsFailedResponse> => {
  const res = await fetch(getMarkTasksAsFailedUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskFailBulkRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: markTasksAsFailedResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as markTasksAsFailedResponse;
};

export const getMarkTasksAsFailedMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markTasksAsFailed>>,
    TError,
    { data: TaskFailBulkRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markTasksAsFailed>>,
  TError,
  { data: TaskFailBulkRequest },
  TContext
> => {
  const mutationKey = ["markTasksAsFailed"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markTasksAsFailed>>,
    { data: TaskFailBulkRequest }
  > = (props) => {
    const { data } = props ?? {};

    return markTasksAsFailed(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkTasksAsFailedMutationResult = NonNullable<
  Awaited<ReturnType<typeof markTasksAsFailed>>
>;
export type MarkTasksAsFailedMutationBody = TaskFailBulkRequest;
export type MarkTasksAsFailedMutationError = HTTPValidationError;

/**
 * @summary Mark many tasks as failed
 */
export const useMarkTasksAsFailed = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markTasksAsFailed>>,
      TError,
      { data: TaskFailBulkRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markTasksAsFailed>>,
  TError,
  { data: TaskFailBulkRequest },
  TContext
> => {
  return useMutation(getMarkTasksAsFailedMutationOptions(options), queryClient);
};
export type createAnalyzeEmergenceTaskResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type createAnalyzeEmergenceTaskResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type createAnalyzeEmergenceTaskResponseSuccess =
  createAnalyzeEmergenceTaskResponse200 & {
    headers: Headers;
  };
export type createAnalyzeEmergenceTaskResponseError =
  createAnalyzeEmergenceTaskResponse422 & {
    headers: Headers;
  };

export type createAnalyzeEmergenceTaskResponse =
  | createAnalyzeEmergenceTaskResponseSuccess
  | createAnalyzeEmergenceTaskResponseError;

export const getCreateAnalyzeEmergenceTaskUrl = () => {
  return `/api/tasks/analyze-emergence/`;
};

/**
 * Creates a analyze_emergence task for an ortho and trial. Row snips are produced by a shared snip_ortho task that is created automatically when none exists (or the latest one failed) and reused otherwise, so running several analyses never re-clips the ortho. The task is waiting until snipping completes, then runs. Returns 422 when the ortho or trial does not exist, the ortho has no stitched image, or any plot in the trial is missing a boundary. Returns 409 when the most recent analyze_emergence task is waiting, pending, or processing. A failed or completed analyze task does not block a retry or re-run. The task fails if snipping fails.
 * @summary Initiate row snip emergence analysis only for a trial
 */
export const createAnalyzeEmergenceTask = async (
  analyzeSnipsRequest: AnalyzeSnipsRequest,
  options?: RequestInit,
): Promise<createAnalyzeEmergenceTaskResponse> => {
  const res = await fetch(getCreateAnalyzeEmergenceTaskUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(analyzeSnipsRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createAnalyzeEmergenceTaskResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createAnalyzeEmergenceTaskResponse;
};

export const getCreateAnalyzeEmergenceTaskMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAnalyzeEmergenceTask>>,
    TError,
    { data: AnalyzeSnipsRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAnalyzeEmergenceTask>>,
  TError,
  { data: AnalyzeSnipsRequest },
  TContext
> => {
  const mutationKey = ["createAnalyzeEmergenceTask"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAnalyzeEmergenceTask>>,
    { data: AnalyzeSnipsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createAnalyzeEmergenceTask(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAnalyzeEmergenceTaskMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAnalyzeEmergenceTask>>
>;
export type CreateAnalyzeEmergenceTaskMutationBody = AnalyzeSnipsRequest;
export type CreateAnalyzeEmergenceTaskMutationError = HTTPValidationError;

/**
 * @summary Initiate row snip emergence analysis only for a trial
 */
export const useCreateAnalyzeEmergenceTask = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAnalyzeEmergenceTask>>,
      TError,
      { data: AnalyzeSnipsRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createAnalyzeEmergenceTask>>,
  TError,
  { data: AnalyzeSnipsRequest },
  TContext
> => {
  return useMutation(
    getCreateAnalyzeEmergenceTaskMutationOptions(options),
    queryClient,
  );
};
export type createAnalyzeCanopyCoverTaskResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type createAnalyzeCanopyCoverTaskResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type createAnalyzeCanopyCoverTaskResponseSuccess =
  createAnalyzeCanopyCoverTaskResponse200 & {
    headers: Headers;
  };
export type createAnalyzeCanopyCoverTaskResponseError =
  createAnalyzeCanopyCoverTaskResponse422 & {
    headers: Headers;
  };

export type createAnalyzeCanopyCoverTaskResponse =
  | createAnalyzeCanopyCoverTaskResponseSuccess
  | createAnalyzeCanopyCoverTaskResponseError;

export const getCreateAnalyzeCanopyCoverTaskUrl = () => {
  return `/api/tasks/analyze-canopy-cover/`;
};

/**
 * Creates a analyze_canopy_cover task for an ortho and trial. Row snips are produced by a shared snip_ortho task that is created automatically when none exists (or the latest one failed) and reused otherwise, so running several analyses never re-clips the ortho. The task is waiting until snipping completes, then runs. Returns 422 when the ortho or trial does not exist, the ortho has no stitched image, or any plot in the trial is missing a boundary. Returns 409 when the most recent analyze_canopy_cover task is waiting, pending, or processing. A failed or completed analyze task does not block a retry or re-run. The task fails if snipping fails.
 * @summary Initiate row snip canopy cover analysis only for a trial
 */
export const createAnalyzeCanopyCoverTask = async (
  analyzeSnipsRequest: AnalyzeSnipsRequest,
  options?: RequestInit,
): Promise<createAnalyzeCanopyCoverTaskResponse> => {
  const res = await fetch(getCreateAnalyzeCanopyCoverTaskUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(analyzeSnipsRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createAnalyzeCanopyCoverTaskResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createAnalyzeCanopyCoverTaskResponse;
};

export const getCreateAnalyzeCanopyCoverTaskMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAnalyzeCanopyCoverTask>>,
    TError,
    { data: AnalyzeSnipsRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAnalyzeCanopyCoverTask>>,
  TError,
  { data: AnalyzeSnipsRequest },
  TContext
> => {
  const mutationKey = ["createAnalyzeCanopyCoverTask"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAnalyzeCanopyCoverTask>>,
    { data: AnalyzeSnipsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createAnalyzeCanopyCoverTask(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAnalyzeCanopyCoverTaskMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAnalyzeCanopyCoverTask>>
>;
export type CreateAnalyzeCanopyCoverTaskMutationBody = AnalyzeSnipsRequest;
export type CreateAnalyzeCanopyCoverTaskMutationError = HTTPValidationError;

/**
 * @summary Initiate row snip canopy cover analysis only for a trial
 */
export const useCreateAnalyzeCanopyCoverTask = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAnalyzeCanopyCoverTask>>,
      TError,
      { data: AnalyzeSnipsRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createAnalyzeCanopyCoverTask>>,
  TError,
  { data: AnalyzeSnipsRequest },
  TContext
> => {
  return useMutation(
    getCreateAnalyzeCanopyCoverTaskMutationOptions(options),
    queryClient,
  );
};
export type updateTasksResponse200 = {
  data: TaskBulkMutationResponse;
  status: 200;
};

export type updateTasksResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type updateTasksResponseSuccess = updateTasksResponse200 & {
  headers: Headers;
};
export type updateTasksResponseError = updateTasksResponse422 & {
  headers: Headers;
};

export type updateTasksResponse =
  updateTasksResponseSuccess | updateTasksResponseError;

export const getUpdateTasksUrl = () => {
  return `/api/tasks/update`;
};

/**
 * Superseded tasks are left unchanged and returned in ``superseded``. Other tasks are updated. Missing ids are returned in ``not_found``.
 * @summary Update many tasks
 */
export const updateTasks = async (
  taskUpdateBulkRequest: TaskUpdateBulkRequest,
  options?: RequestInit,
): Promise<updateTasksResponse> => {
  const res = await fetch(getUpdateTasksUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskUpdateBulkRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: updateTasksResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as updateTasksResponse;
};

export const getUpdateTasksMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTasks>>,
    TError,
    { data: TaskUpdateBulkRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTasks>>,
  TError,
  { data: TaskUpdateBulkRequest },
  TContext
> => {
  const mutationKey = ["updateTasks"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTasks>>,
    { data: TaskUpdateBulkRequest }
  > = (props) => {
    const { data } = props ?? {};

    return updateTasks(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTasksMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTasks>>
>;
export type UpdateTasksMutationBody = TaskUpdateBulkRequest;
export type UpdateTasksMutationError = HTTPValidationError;

/**
 * @summary Update many tasks
 */
export const useUpdateTasks = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateTasks>>,
      TError,
      { data: TaskUpdateBulkRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateTasks>>,
  TError,
  { data: TaskUpdateBulkRequest },
  TContext
> => {
  return useMutation(getUpdateTasksMutationOptions(options), queryClient);
};
export type getTaskByIdResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type getTaskByIdResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getTaskByIdResponseSuccess = getTaskByIdResponse200 & {
  headers: Headers;
};
export type getTaskByIdResponseError = getTaskByIdResponse422 & {
  headers: Headers;
};

export type getTaskByIdResponse =
  getTaskByIdResponseSuccess | getTaskByIdResponseError;

export const getGetTaskByIdUrl = (taskId: number) => {
  return `/api/tasks/${taskId}`;
};

/**
 * @summary Get Task By Id
 */
export const getTaskById = async (
  taskId: number,
  options?: RequestInit,
): Promise<getTaskByIdResponse> => {
  const res = await fetch(getGetTaskByIdUrl(taskId), {
    ...options,
    method: "GET",
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: getTaskByIdResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getTaskByIdResponse;
};

export const getGetTaskByIdQueryKey = (taskId: number) => {
  return [`/api/tasks/${taskId}`] as const;
};

export const getGetTaskByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskById>>,
  TError = HTTPValidationError,
>(
  taskId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTaskById>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
) => {
  const { query: queryOptions, fetch: fetchOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTaskByIdQueryKey(taskId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskById>>> = ({
    signal,
  }) => getTaskById(taskId, { signal, ...fetchOptions });

  return {
    queryKey,
    queryFn,
    enabled: taskId !== null && taskId !== undefined,
    staleTime: 10000,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTaskById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTaskByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskById>>
>;
export type GetTaskByIdQueryError = HTTPValidationError;

export function useGetTaskById<
  TData = Awaited<ReturnType<typeof getTaskById>>,
  TError = HTTPValidationError,
>(
  taskId: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTaskById>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskById>>,
          TError,
          Awaited<ReturnType<typeof getTaskById>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTaskById<
  TData = Awaited<ReturnType<typeof getTaskById>>,
  TError = HTTPValidationError,
>(
  taskId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTaskById>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskById>>,
          TError,
          Awaited<ReturnType<typeof getTaskById>>
        >,
        "initialData"
      >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTaskById<
  TData = Awaited<ReturnType<typeof getTaskById>>,
  TError = HTTPValidationError,
>(
  taskId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTaskById>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Task By Id
 */

export function useGetTaskById<
  TData = Awaited<ReturnType<typeof getTaskById>>,
  TError = HTTPValidationError,
>(
  taskId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTaskById>>, TError, TData>
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetTaskByIdQueryOptions(taskId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateTaskByIdResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type updateTaskByIdResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type updateTaskByIdResponseSuccess = updateTaskByIdResponse200 & {
  headers: Headers;
};
export type updateTaskByIdResponseError = updateTaskByIdResponse422 & {
  headers: Headers;
};

export type updateTaskByIdResponse =
  updateTaskByIdResponseSuccess | updateTaskByIdResponseError;

export const getUpdateTaskByIdUrl = (taskId: number) => {
  return `/api/tasks/${taskId}`;
};

/**
 * Returns 409 when the task is superseded.
 * @summary Update Task By Id
 */
export const updateTaskById = async (
  taskId: number,
  taskUpdate: TaskUpdate,
  options?: RequestInit,
): Promise<updateTaskByIdResponse> => {
  const res = await fetch(getUpdateTaskByIdUrl(taskId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskUpdate),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: updateTaskByIdResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as updateTaskByIdResponse;
};

export const getUpdateTaskByIdMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTaskById>>,
    TError,
    { taskId: number; data: TaskUpdate },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTaskById>>,
  TError,
  { taskId: number; data: TaskUpdate },
  TContext
> => {
  const mutationKey = ["updateTaskById"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTaskById>>,
    { taskId: number; data: TaskUpdate }
  > = (props) => {
    const { taskId, data } = props ?? {};

    return updateTaskById(taskId, data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTaskByIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTaskById>>
>;
export type UpdateTaskByIdMutationBody = TaskUpdate;
export type UpdateTaskByIdMutationError = HTTPValidationError;

/**
 * @summary Update Task By Id
 */
export const useUpdateTaskById = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateTaskById>>,
      TError,
      { taskId: number; data: TaskUpdate },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateTaskById>>,
  TError,
  { taskId: number; data: TaskUpdate },
  TContext
> => {
  return useMutation(getUpdateTaskByIdMutationOptions(options), queryClient);
};
export type markTaskAsCompletedResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type markTaskAsCompletedResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type markTaskAsCompletedResponseSuccess =
  markTaskAsCompletedResponse200 & {
    headers: Headers;
  };
export type markTaskAsCompletedResponseError =
  markTaskAsCompletedResponse422 & {
    headers: Headers;
  };

export type markTaskAsCompletedResponse =
  markTaskAsCompletedResponseSuccess | markTaskAsCompletedResponseError;

export const getMarkTaskAsCompletedUrl = (taskId: number) => {
  return `/api/tasks/${taskId}/complete`;
};

/**
 * Returns 409 when the task is superseded.
 * @summary Mark Task As Completed
 */
export const markTaskAsCompleted = async (
  taskId: number,
  taskResultsInput: TaskResultsInput,
  options?: RequestInit,
): Promise<markTaskAsCompletedResponse> => {
  const res = await fetch(getMarkTaskAsCompletedUrl(taskId), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskResultsInput),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: markTaskAsCompletedResponse["data"] = body
    ? JSON.parse(body)
    : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as markTaskAsCompletedResponse;
};

export const getMarkTaskAsCompletedMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markTaskAsCompleted>>,
    TError,
    { taskId: number; data: TaskResultsInput },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markTaskAsCompleted>>,
  TError,
  { taskId: number; data: TaskResultsInput },
  TContext
> => {
  const mutationKey = ["markTaskAsCompleted"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markTaskAsCompleted>>,
    { taskId: number; data: TaskResultsInput }
  > = (props) => {
    const { taskId, data } = props ?? {};

    return markTaskAsCompleted(taskId, data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkTaskAsCompletedMutationResult = NonNullable<
  Awaited<ReturnType<typeof markTaskAsCompleted>>
>;
export type MarkTaskAsCompletedMutationBody = TaskResultsInput;
export type MarkTaskAsCompletedMutationError = HTTPValidationError;

/**
 * @summary Mark Task As Completed
 */
export const useMarkTaskAsCompleted = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markTaskAsCompleted>>,
      TError,
      { taskId: number; data: TaskResultsInput },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markTaskAsCompleted>>,
  TError,
  { taskId: number; data: TaskResultsInput },
  TContext
> => {
  return useMutation(
    getMarkTaskAsCompletedMutationOptions(options),
    queryClient,
  );
};
export type markTaskAsFailedResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type markTaskAsFailedResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type markTaskAsFailedResponseSuccess = markTaskAsFailedResponse200 & {
  headers: Headers;
};
export type markTaskAsFailedResponseError = markTaskAsFailedResponse422 & {
  headers: Headers;
};

export type markTaskAsFailedResponse =
  markTaskAsFailedResponseSuccess | markTaskAsFailedResponseError;

export const getMarkTaskAsFailedUrl = (
  taskId: number,
  params?: MarkTaskAsFailedParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/tasks/${taskId}/fail?${stringifiedParams}`
    : `/api/tasks/${taskId}/fail`;
};

/**
 * Returns 409 when the task is superseded.
 * @summary Mark Task As Failed
 */
export const markTaskAsFailed = async (
  taskId: number,
  taskResultsInput: TaskResultsInput,
  params?: MarkTaskAsFailedParams,
  options?: RequestInit,
): Promise<markTaskAsFailedResponse> => {
  const res = await fetch(getMarkTaskAsFailedUrl(taskId, params), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(taskResultsInput),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: markTaskAsFailedResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as markTaskAsFailedResponse;
};

export const getMarkTaskAsFailedMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markTaskAsFailed>>,
    TError,
    { taskId: number; data: TaskResultsInput; params?: MarkTaskAsFailedParams },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markTaskAsFailed>>,
  TError,
  { taskId: number; data: TaskResultsInput; params?: MarkTaskAsFailedParams },
  TContext
> => {
  const mutationKey = ["markTaskAsFailed"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markTaskAsFailed>>,
    { taskId: number; data: TaskResultsInput; params?: MarkTaskAsFailedParams }
  > = (props) => {
    const { taskId, data, params } = props ?? {};

    return markTaskAsFailed(taskId, data, params, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkTaskAsFailedMutationResult = NonNullable<
  Awaited<ReturnType<typeof markTaskAsFailed>>
>;
export type MarkTaskAsFailedMutationBody = TaskResultsInput;
export type MarkTaskAsFailedMutationError = HTTPValidationError;

/**
 * @summary Mark Task As Failed
 */
export const useMarkTaskAsFailed = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markTaskAsFailed>>,
      TError,
      {
        taskId: number;
        data: TaskResultsInput;
        params?: MarkTaskAsFailedParams;
      },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markTaskAsFailed>>,
  TError,
  { taskId: number; data: TaskResultsInput; params?: MarkTaskAsFailedParams },
  TContext
> => {
  return useMutation(getMarkTaskAsFailedMutationOptions(options), queryClient);
};
